import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Input } from 'components/FormComponents/FormComponents';
import { useFreecodeContextRun, useFreecodeContextData, useFreecodeContextLayout } from 'hooks/useContext';
import { Modal, Header, Body, Footer, FooterButton } from '../modalElements';
import GDPRModal from '../GDPRModal';
import FreecodeAuthModal from './FreecodeAuthModal/FreecodeAuthModal';
import services from 'api/services';
import { sendAnalytics } from 'functions/sendAnalytics';

const { GDPRService, GDPRAgreementService, FreecodeSubmitService } = services;

// Submit modal intended for use with challenges
export default function SubmitChallengeModal({ contest, isOpen = true }) {
  const { user, userRole, isTicketAuth, isCommunityUser } = useSelector((state) => state.auth);

  const { setDisplaySubmit } = useFreecodeContextLayout();
  const { setTest, test, sponsor, languageData } = useFreecodeContextData();
  const { codeSolRef, languageRef } = useFreecodeContextRun();

  const navigate = useNavigate();

  const [teamName, setTeamName] = useState(test?.hackathonTeamName || '');
  const [gdprRequired, setGdprRequired] = useState(true);
  const [gdprAgreement, setGdprAgreement] = useState(undefined);
  const [gdprLoading, setGdprLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onCancel = () => setDisplaySubmit(false);

  useEffect(() => {
    if (sponsor?.type !== 'contestSponsor') return setGdprRequired(false);
    if (!userRole) return;

    // content is sponsored and requires GDPR consent
    const fetchSponsorGdprInfo = async () => {
      try {
        let hasAgreed = await GDPRService.hasCandidateCompanyConsent(sponsor.companyId);
        if (hasAgreed) return setGdprRequired(false);
        let agreement = await GDPRAgreementService.getCompanyGDPRAgreement(sponsor.companyId);
        setGdprAgreement(agreement);
        setGdprLoading(false);
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'GDPR error');
      }
    };
    fetchSponsorGdprInfo();
  }, [userRole, sponsor]);

  const confirmGdpr = (value) => {
    setGdprLoading(true);

    const doConfirmGdpr = async () => {
      try {
        let gdprAgreementConsent = await GDPRService.setGDPR(
          sponsor.companyId,
          value ? { conditionalRetention: value } : {}
        );
        if (gdprAgreementConsent) {
          toast.success('GDPR agreed');
          setGdprRequired(false);
        }
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'GDPR error');
      }
      setGdprLoading(false);
    };
    doConfirmGdpr();
  };

  const onSubmit = (stayOnPage) => {
    if (contest.isHackathon && teamName) setTest((oldTest) => ({ ...oldTest, hackathonTeamName: teamName }));
    if (isSubmitting) return;
    setIsSubmitting(true);
    sendAnalytics('submitChallenge', contest._id);
    const doSubmit = async () => {
      try {
        const submitResult = await FreecodeSubmitService.submitContest({
          contest: contest._id,
          code: codeSolRef.current,
          language: languageRef.current,
          languageVersion: languageData[languageRef.current].CurrentVersion,
          teamName: teamName,
        });
        if (!submitResult?.submissionId) throw new Error('Response not valid');

        if (submitResult.event?.amount > 0)
          toast.info(submitResult.event.description || `${submitResult.event.amount} XP Awarded`);

        if (stayOnPage) onCancel();
        else navigate(`/challenges/${submitResult.contest || contest._id}`);
      } catch (error) {
        toast.error(error?.message || 'Error submitting contest');
      } finally {
        setIsSubmitting(false);
      }
    };

    doSubmit();
  };

  const hackathonSubmit = () => (
    <Modal isOpen={isOpen}>
      <Header>Submit</Header>
      <Body loading={isSubmitting}>
        <p>
          Are you sure you want to submit your solution? You can submit multiple times but you only compete with your
          last submission.
        </p>
        <p>Enter a team name or submit as yourself.</p>
        <Input
          type="text"
          autoFocus
          value={teamName}
          placeholder={user?.coderCommunityProfile?.name || user?.name || 'unknown'}
          onChange={(e) => setTeamName(e.target.value)}
        />
      </Body>
      <Footer>
        <FooterButton onClick={onCancel} label="Cancel" />
        <FooterButton onClick={() => onSubmit(true)} label="Submit and Continue" disabled={isSubmitting} />
        <FooterButton onClick={() => onSubmit(false)} label="Submit and Exit" disabled={isSubmitting} />
      </Footer>
    </Modal>
  );

  const challengeSubmit = () => (
    <Modal isOpen={isOpen}>
      <Header>Submit</Header>
      <Body loading={isSubmitting}>
        <p>
          Are you sure you want to submit your solution? You can submit multiple times but you only compete with your
          last submission.
        </p>
      </Body>
      <Footer>
        <FooterButton onClick={onCancel} label="Cancel" />
        <FooterButton onClick={() => onSubmit(true)} label="Submit and Continue" disabled={isSubmitting} />
        <FooterButton onClick={() => onSubmit(false)} label="Submit and Exit" disabled={isSubmitting} />
      </Footer>
    </Modal>
  );

  return !userRole || isTicketAuth || !isCommunityUser ? (
    <FreecodeAuthModal onCancel={onCancel} />
  ) : gdprRequired ? (
    <GDPRModal onConfirm={(value) => confirmGdpr(value)} gdprAgreement={gdprAgreement} loading={gdprLoading} />
  ) : contest.isHackathon ? (
    hackathonSubmit()
  ) : (
    challengeSubmit()
  );
}
